import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, Sparkles, Download, RefreshCw, Video, FolderOpen, Upload, ChevronDown, ChevronUp, Trash2, Play, Clock, Zap, Image as ImageIcon, Palette } from 'lucide-react';
import { toast } from 'sonner';
import { StudioCanvas } from './StudioCanvas';

const API_URL = window.location.origin;

const getToken = () => localStorage.getItem('admin_token');
const authHeaders = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` });

// ── Constants ──────────────────────────────────────────────────────────────
const BRANDS = ['Apple', 'Samsung', 'Huawei', 'Xiaomi', 'OnePlus', 'Google', 'Oppo', 'Sony', 'LG', 'Motorola'];
const REPAIRS = ['écran cassé', 'batterie', 'connecteur de charge', 'caméra', 'haut-parleur', 'bouton power', 'micro-soudure', 'vitre arrière'];

const STYLES = [
  { id: 'realistic', label: 'Réaliste', icon: '📷' },
  { id: 'cinema', label: 'Cinéma', icon: '🎬' },
  { id: 'minimal', label: 'Minimaliste', icon: '⬜' },
  { id: 'neon', label: 'Néon', icon: '✨' },
  { id: 'studio_pro', label: 'Studio Pro', icon: '🏆' },
];

const QUALITIES = [
  { id: 'low', label: 'Standard', desc: '~10s' },
  { id: 'medium', label: 'HD', desc: '~20s' },
  { id: 'high', label: 'Ultra', desc: '~40s' },
];

const FORMATS_IMG = [
  { id: '1024x1024', label: '1:1', desc: 'Instagram' },
  { id: '1024x1536', label: '9:16', desc: 'Stories' },
  { id: '1536x1024', label: '16:9', desc: 'Facebook' },
];

const EFFECTS = [
  { id: 'white_bg', label: 'Fond blanc' },
  { id: 'dark_bg', label: 'Fond sombre' },
  { id: 'gradient', label: 'Dégradé' },
  { id: 'bokeh', label: 'Bokeh' },
];

const VIDEO_DURATIONS = [
  { id: 4, label: '4s', desc: 'Rapide' },
  { id: 8, label: '8s', desc: 'Normal' },
  { id: 12, label: '12s', desc: 'Long' },
];

const VIDEO_FORMATS = [
  { id: '1280x720',  label: '16:9',  desc: 'YouTube / Facebook' },
  { id: '1792x1024', label: '21:9',  desc: 'Cinématique' },
  { id: '1024x1792', label: '9:16',  desc: 'Reels / Shorts' },
];

const VIDEO_MODELS = [
  { id: 'sora-2', label: 'Sora 2', desc: 'Standard (~2-3 min)' },
  { id: 'sora-2-pro', label: 'Sora 2 Pro', desc: 'Haute qualité (~5 min)' },
];

// ── Utility ────────────────────────────────────────────────────────────────
// Compress image to max 1280px and quality 0.82 to keep it under 800KB
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const MAX = 1280;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
          else { width = Math.round(width * MAX / height); height = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.82));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

const UploadZone = ({ label, value, onChange, id }) => {
  const ref = useRef();
  return (
    <div>
      <label className="text-gray-400 text-sm block mb-2">{label}</label>
      <div
        onClick={() => ref.current?.click()}
        className={`relative flex flex-col items-center justify-center rounded-2xl border-2 border-dashed cursor-pointer transition-all min-h-[120px] overflow-hidden
          ${value ? 'border-purple-500 bg-purple-900/10' : 'border-gray-600 bg-gray-900/50 hover:border-purple-400 hover:bg-purple-900/5'}`}
      >
        {value ? (
          <img src={value} alt="upload" className="w-full h-40 object-cover rounded-xl" />
        ) : (
          <div className="flex flex-col items-center gap-2 py-6 px-4">
            <Upload size={28} className="text-gray-500" />
            <span className="text-gray-400 text-sm text-center">Appuyez pour uploader</span>
            <span className="text-gray-600 text-xs">JPG, PNG, HEIC</span>
          </div>
        )}
        {value && (
          <button
            onClick={(e) => { e.stopPropagation(); onChange(null); }}
            className="absolute top-2 right-2 bg-red-600/80 text-white rounded-full p-1"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>
      <input
        ref={ref}
        id={id}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (f) onChange(await fileToBase64(f));
          e.target.value = '';
        }}
      />
    </div>
  );
};

const AdvancedControls = ({ style, setStyle, quality, setQuality, format, setFormat, effects, setEffects, formats = FORMATS_IMG }) => {
  const [open, setOpen] = useState(false);
  const toggleEffect = (id) => setEffects(prev => prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]);

  return (
    <div className="border border-gray-700 rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-gray-300 text-sm font-medium hover:bg-gray-800/50 transition-colors"
      >
        <span className="flex items-center gap-2"><Zap size={15} className="text-purple-400" />Contrôles avancés</span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-4 bg-gray-800/30">
          {/* Style */}
          <div>
            <label className="text-gray-400 text-xs block mb-2">Style</label>
            <div className="flex flex-wrap gap-2">
              {STYLES.map(s => (
                <button key={s.id} onClick={() => setStyle(s.id)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all ${style === s.id ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}>
                  {s.icon} {s.label}
                </button>
              ))}
            </div>
          </div>
          {/* Qualité */}
          <div>
            <label className="text-gray-400 text-xs block mb-2">Qualité</label>
            <div className="flex gap-2">
              {QUALITIES.map(q => (
                <button key={q.id} onClick={() => setQuality(q.id)}
                  className={`flex-1 py-2 rounded-xl text-xs font-medium transition-all text-center ${quality === q.id ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}>
                  <div>{q.label}</div><div className="opacity-60">{q.desc}</div>
                </button>
              ))}
            </div>
          </div>
          {/* Format */}
          <div>
            <label className="text-gray-400 text-xs block mb-2">Format</label>
            <div className="flex gap-2">
              {formats.map(f => (
                <button key={f.id} onClick={() => setFormat(f.id)}
                  className={`flex-1 py-2 rounded-xl text-xs font-medium transition-all text-center ${format === f.id ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}>
                  <div>{f.label}</div><div className="opacity-60">{f.desc}</div>
                </button>
              ))}
            </div>
          </div>
          {/* Effets */}
          <div>
            <label className="text-gray-400 text-xs block mb-2">Effets de fond</label>
            <div className="flex flex-wrap gap-2">
              {EFFECTS.map(e => (
                <button key={e.id} onClick={() => toggleEffect(e.id)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all ${effects.includes(e.id) ? 'bg-orange-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}>
                  {e.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ResultPanel = ({ image, onDownload, onSave, onRegenerate, generating, label }) => {
  if (!image) return (
    <div className="flex flex-col items-center justify-center h-52 rounded-2xl border-2 border-dashed border-gray-700 text-center">
      <Camera size={40} className="text-gray-700 mb-3" />
      <p className="text-gray-500 text-sm">Le résultat apparaîtra ici</p>
    </div>
  );
  return (
    <div className="space-y-3">
      <div className="relative rounded-2xl overflow-hidden border border-purple-500/30">
        <img src={image} alt="résultat" className="w-full h-auto max-h-80 object-contain bg-gray-900" />
        <span className="absolute top-2 left-2 bg-purple-600 text-white text-xs px-2 py-0.5 rounded-full font-bold">IA</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <button onClick={onDownload}
          className="flex items-center justify-center gap-1.5 bg-green-700 hover:bg-green-800 text-white rounded-xl py-2.5 text-sm font-medium transition-all active:scale-95">
          <Download size={15} /> Télécharger
        </button>
        <button onClick={onSave}
          className="flex items-center justify-center gap-1.5 bg-blue-700 hover:bg-blue-800 text-white rounded-xl py-2.5 text-sm font-medium transition-all active:scale-95">
          <FolderOpen size={15} /> Sauvegarder
        </button>
        <button onClick={onRegenerate} disabled={generating}
          className="flex items-center justify-center gap-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-xl py-2.5 text-sm font-medium transition-all active:scale-95 disabled:opacity-50">
          <RefreshCw size={15} className={generating ? 'animate-spin' : ''} /> Regénérer
        </button>
      </div>
    </div>
  );
};

// ── PHOTO TAB ──────────────────────────────────────────────────────────────
const PhotoTab = () => {
  const [mode, setMode] = useState('text'); // 'text' | 'upload_after' | 'marketing'
  const [brand, setBrand] = useState('Apple');
  const [model, setModel] = useState('iPhone 14');
  const [repair, setRepair] = useState('écran cassé');
  const [description, setDescription] = useState('');
  const [beforeImage, setBeforeImage] = useState(null);
  const [afterImage, setAfterImage] = useState(null);
  const [style, setStyle] = useState('realistic');
  const [quality, setQuality] = useState('high');
  const [format, setFormat] = useState('1024x1024');
  const [effects, setEffects] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  const analyzePhoto = async (base64) => {
    if (!base64) return;
    setAnalyzing(true);
    try {
      const resp = await fetch(`${API_URL}/api/studio/analyze-photo`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ image_base64: base64 })
      });
      if (resp.ok) {
        const data = await resp.json();
        const a = data.analysis || {};
        setAnalysis(a);
        if (a.brand) setBrand(a.brand);
        if (a.model) setModel(a.model);
        if (a.repair_type) {
          const found = REPAIRS.find(r => a.repair_type.toLowerCase().includes(r.split(' ')[0]));
          if (found) setRepair(found);
        }
        toast.success('Photo analysée automatiquement !');
      }
    } catch (e) {
      console.warn('Analyse auto échouée:', e);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleBeforeUpload = (b64) => {
    setBeforeImage(b64);
    if (b64) analyzePhoto(b64);
  };

  const generate = async () => {
    setGenerating(true);
    setResult(null);
    try {
      let endpoint, body;

      if (mode === 'text') {
        endpoint = `/api/studio/generate-from-upload`;
        body = { brand, model, repair_type: repair, description, style, quality, effects };
      } else if (mode === 'upload_after') {
        if (!beforeImage) { toast.error('Uploadez une photo Avant'); setGenerating(false); return; }
        endpoint = `/api/studio/generate-after-from-photo`;
        body = { image_base64: beforeImage, brand, model, repair_type: repair, style, quality, effects };
      } else if (mode === 'marketing') {
        if (!beforeImage || !afterImage) { toast.error('Uploadez les photos Avant ET Après'); setGenerating(false); return; }
        endpoint = `/api/studio/generate-marketing-card`;
        body = { before_image_base64: beforeImage, after_image_base64: afterImage, brand, model, repair_type: repair, style, quality, effects };
      }

      const resp = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(body)
      });

      if (!resp.ok) {
        if (resp.status === 404) throw new Error('Endpoint non trouvé — déployez le site sur phonelab69.fr');
        const d = await resp.json().catch(() => ({}));
        throw new Error(d.detail || `Erreur serveur (${resp.status})`);
      }
      const data = await resp.json();
      setResult(`data:image/png;base64,${data.image_base64}`);
      toast.success('Image générée !');
    } catch (e) {
      toast.error(`Erreur: ${e.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const download = () => {
    if (!result) return;
    const a = document.createElement('a');
    a.href = result;
    a.download = `phonelab-${brand}-${model}-${repair}.png`.replace(/\s+/g, '-').toLowerCase();
    a.click();
  };

  const save = async () => {
    if (!result) return;
    try {
      await fetch(`${API_URL}/api/studio/projects/save`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          type: 'image', subtype: mode,
          thumbnail_base64: result,
          full_base64: result,
          label: `${brand} ${model} — ${repair}`,
          metadata: { brand, model, repair, style, quality, mode }
        })
      });
      toast.success('Projet sauvegardé !');
    } catch (e) {
      toast.error('Erreur sauvegarde');
    }
  };

  const MODES = [
    { id: 'text', label: '✍️ Texte', desc: 'Description → Image' },
    { id: 'upload_after', label: '📱 Upload Avant', desc: 'Photo cassée → IA génère après' },
    { id: 'marketing', label: '📱📱 Avant + Après', desc: 'Vos 2 photos → Carte marketing' },
  ];

  return (
    <div className="space-y-4">
      {/* Mode selector */}
      <div className="space-y-2">
        <label className="text-gray-400 text-xs font-medium uppercase tracking-wider">Mode de génération</label>
        <div className="grid grid-cols-1 gap-2">
          {MODES.map(m => (
            <button key={m.id} onClick={() => { setMode(m.id); setResult(null); }}
              className={`flex items-center gap-3 px-4 py-3 rounded-2xl border-2 text-left transition-all active:scale-[0.98]
                ${mode === m.id ? 'border-purple-500 bg-purple-900/20 text-white' : 'border-gray-700 text-gray-400 hover:border-gray-600'}`}
            >
              <span className="text-base font-semibold">{m.label}</span>
              <span className="text-xs opacity-60">{m.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Upload zones */}
      {mode === 'upload_after' && (
        <div className="relative">
          <UploadZone label="Photo du téléphone cassé (Avant)" value={beforeImage} onChange={handleBeforeUpload} id="before1" />
          {analyzing && (
            <div className="mt-2 flex items-center gap-2 text-purple-400 text-xs">
              <div className="animate-spin rounded-full h-3 w-3 border border-purple-400 border-t-transparent" />
              Analyse automatique en cours...
            </div>
          )}
          {analysis && (
            <div className="mt-2 bg-purple-900/20 border border-purple-500/30 rounded-xl p-3 text-xs text-gray-300">
              Détecté : <span className="text-white font-medium">{analysis.brand} {analysis.model}</span> — {analysis.damage?.substring(0, 80)}
            </div>
          )}
        </div>
      )}
      {mode === 'marketing' && (
        <div className="grid grid-cols-2 gap-3">
          <UploadZone label="📵 AVANT (cassé)" value={beforeImage} onChange={handleBeforeUpload} id="before2" />
          <UploadZone label="✅ APRÈS (réparé)" value={afterImage} onChange={setAfterImage} id="after2" />
        </div>
      )}

      {/* Device info */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-gray-400 text-xs block mb-1.5">Marque</label>
          <select value={brand} onChange={e => setBrand(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-purple-400">
            {BRANDS.map(b => <option key={b}>{b}</option>)}
          </select>
        </div>
        <div>
          <label className="text-gray-400 text-xs block mb-1.5">Modèle</label>
          <input type="text" value={model} onChange={e => setModel(e.target.value)}
            placeholder="iPhone 14 Pro..."
            className="w-full bg-gray-800 border border-gray-700 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-purple-400" />
        </div>
      </div>
      <div>
        <label className="text-gray-400 text-xs block mb-1.5">Type de réparation</label>
        <select value={repair} onChange={e => setRepair(e.target.value)}
          className="w-full bg-gray-800 border border-gray-700 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-purple-400">
          {REPAIRS.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
        </select>
      </div>
      {mode === 'text' && (
        <div>
          <label className="text-gray-400 text-xs block mb-1.5">Contexte additionnel <span className="opacity-50">(optionnel)</span></label>
          <input type="text" value={description} onChange={e => setDescription(e.target.value)}
            placeholder="ex: très cassé, couleur noire..."
            className="w-full bg-gray-800 border border-gray-700 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-purple-400" />
        </div>
      )}

      {/* Advanced controls */}
      <AdvancedControls style={style} setStyle={setStyle} quality={quality} setQuality={setQuality}
        format={format} setFormat={setFormat} effects={effects} setEffects={setEffects} />

      {/* Generate button */}
      <button onClick={generate} disabled={generating} data-testid="generate-photo-btn"
        className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2 text-base transition-all active:scale-[0.98]">
        {generating ? (
          <><div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /><span>Génération IA... (~20-40s)</span></>
        ) : (
          <><Sparkles size={18} /><span>Générer avec IA</span></>
        )}
      </button>

      {/* Result */}
      <ResultPanel image={result} onDownload={download} onSave={save} onRegenerate={generate} generating={generating} />
    </div>
  );
};

// ── VIDEO TAB ──────────────────────────────────────────────────────────────
const CAPCUT_SYSTEM_PROMPT = `Applique le montage CapCut fidèle sur ces photos réelles, transition fluide entre l'écran brisé et l'écran neuf.`;

const VideoTab = () => {
  const [videoMode, setVideoMode] = useState('capcut'); // 'capcut' | 'text' | 'photo'
  const [prompt, setPrompt] = useState('');
  const [refImage, setRefImage] = useState(null);
  const [beforeImage, setBeforeImage] = useState(null);
  const [afterImage, setAfterImage] = useState(null);
  const [brand, setBrand] = useState('Samsung');
  const [model, setModel] = useState('Z Flip');
  const [repair, setRepair] = useState('écran cassé');
  const [duration, setDuration] = useState(8);
  const [format, setFormat] = useState('1280x720');
  const [videoModel, setVideoModel] = useState('sora-2');
  const [generating, setGenerating] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [videoB64, setVideoB64] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [capCutAnalysis, setCapCutAnalysis] = useState(null);
  const pollRef = useRef(null);
  const timerRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const startVideoJob = async () => {
    // Validation selon le mode
    if (videoMode === 'capcut') {
      if (!beforeImage || !afterImage) { toast.error('Uploadez les deux photos Avant ET Après'); return; }
    } else if (!prompt.trim() && !refImage) {
      toast.error('Décrivez la vidéo ou uploadez une image'); return;
    }

    setGenerating(true);
    setJobId(null);
    setJobStatus(null);
    setVideoB64(null);
    setElapsed(0);
    setCapCutAnalysis(null);
    stopPolling();

    try {
      let endpoint, body;

      if (videoMode === 'capcut') {
        endpoint = '/api/studio/generate-capcut-video';
        body = {
          before_image_base64: beforeImage,
          after_image_base64: afterImage,
          brand, model, repair_type: repair,
          duration, size: format, model_video: videoModel
        };
      } else {
        endpoint = '/api/studio/generate-video';
        body = { prompt: prompt || 'PhoneLab 69 phone repair showcase', duration, size: format, model: videoModel };
        if (refImage) body.image_base64 = refImage;
      }

      const resp = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(body)
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(d.detail || `Erreur ${resp.status}`);
      }
      const data = await resp.json();
      const jid = data.job_id;
      setJobId(jid);
      setJobStatus('processing');
      if (data.analysis) setCapCutAnalysis(data.analysis);
      toast.info(`Job CapCut lancé — ~${Math.round(data.estimated_seconds / 60)} min`);

      timerRef.current = setInterval(() => setElapsed(s => s + 1), 1000);

      pollRef.current = setInterval(async () => {
        try {
          const sr = await fetch(`${API_URL}/api/studio/video-status/${jid}`, { headers: authHeaders() });
          if (sr.ok) {
            const sd = await sr.json();
            if (sd.status === 'completed') {
              stopPolling();
              setJobStatus('completed');
              setVideoB64(sd.video_base64);
              setGenerating(false);
              toast.success('Vidéo CapCut générée !');
            } else if (sd.status === 'failed') {
              stopPolling();
              setJobStatus('failed');
              setGenerating(false);
              toast.error(`Échec: ${sd.error || 'Erreur inconnue'}`);
            }
          }
        } catch (e) { console.warn('Polling error', e); }
      }, 10000);

    } catch (e) {
      setGenerating(false);
      toast.error(`Erreur: ${e.message}`);
    }
  };

  const downloadVideo = () => {
    if (!videoB64) return;
    const a = document.createElement('a');
    a.href = `data:video/mp4;base64,${videoB64}`;
    a.download = `phonelab-video-${jobId}.mp4`;
    a.click();
  };

  const saveVideo = async () => {
    if (!videoB64) return;
    try {
      await fetch(`${API_URL}/api/studio/projects/save`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          type: 'video', subtype: videoMode,
          thumbnail_base64: '',
          full_base64: `data:video/mp4;base64,${videoB64}`,
          label: `Vidéo ${duration}s — ${prompt.substring(0, 40)}`,
          metadata: { prompt, duration, format, model: videoModel }
        })
      });
      toast.success('Vidéo sauvegardée !');
    } catch (e) { toast.error('Erreur sauvegarde'); }
  };

  const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="space-y-4">
      {/* Mode selector */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { id: 'capcut', label: '🎬 CapCut', desc: 'Avant+Après → Morphing', color: 'red' },
          { id: 'text', label: '✍️ Texte', desc: 'Description → Vidéo', color: 'orange' },
          { id: 'photo', label: '📱 Photo', desc: 'Image → Vidéo', color: 'orange' },
        ].map(m => (
          <button key={m.id} onClick={() => { setVideoMode(m.id); setJobStatus(null); setVideoB64(null); }}
            className={`flex flex-col items-center px-2 py-3 rounded-2xl border-2 text-center transition-all active:scale-[0.98]
              ${videoMode === m.id
                ? m.color === 'red' ? 'border-red-500 bg-red-900/20 text-white' : 'border-orange-500 bg-orange-900/20 text-white'
                : 'border-gray-700 text-gray-400 hover:border-gray-600'}`}>
            <span className="font-semibold text-sm">{m.label}</span>
            <span className="text-xs opacity-60 mt-0.5 leading-tight">{m.desc}</span>
          </button>
        ))}
      </div>

      {/* ── MODE CAPCUT ── */}
      {videoMode === 'capcut' && (
        <div className="space-y-4">
          <div className="bg-red-900/20 border border-red-500/30 rounded-2xl p-3">
            <p className="text-red-300 text-xs font-semibold mb-1">🎬 Moteur CapCut — Fidélité 0.95+</p>
            <p className="text-gray-400 text-xs leading-relaxed">
              Morphing fluide entre vos 2 vraies photos. Fissures disparaissent progressivement.
              Zoom Ken Burns 10% max. Zéro hallucination.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <UploadZone label="📵 AVANT (cassé)" value={beforeImage} onChange={setBeforeImage} id="cap-before" />
            <UploadZone label="✅ APRÈS (réparé)" value={afterImage} onChange={setAfterImage} id="cap-after" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs block mb-1.5">Marque</label>
              <select value={brand} onChange={e => setBrand(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400">
                {BRANDS.map(b => <option key={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-xs block mb-1.5">Modèle</label>
              <input type="text" value={model} onChange={e => setModel(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400" />
            </div>
          </div>
          <div>
            <label className="text-gray-400 text-xs block mb-1.5">Type de réparation</label>
            <select value={repair} onChange={e => setRepair(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400">
              {REPAIRS.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
            </select>
          </div>

          {capCutAnalysis && (
            <div className="bg-purple-900/20 border border-purple-500/30 rounded-xl p-3 text-xs text-gray-300">
              <span className="text-purple-300 font-medium">Analyse Gemini Vision : </span>
              {capCutAnalysis.device_color} • {capCutAnalysis.damage_zone}
            </div>
          )}

          <div className="bg-gray-800/40 border border-gray-700 rounded-xl p-3">
            <p className="text-gray-500 text-xs mb-1">Prompt système actif (instructions CapCut)</p>
            <p className="text-gray-400 text-xs italic">"{CAPCUT_SYSTEM_PROMPT}"</p>
          </div>
        </div>
      )}

      {/* ── MODE TEXTE ── */}
      {videoMode === 'text' && (
        <div>
          <label className="text-gray-400 text-xs block mb-1.5">Description de la vidéo</label>
          <textarea value={prompt} onChange={e => setPrompt(e.target.value)}
            placeholder="ex: Technicien répare un iPhone 14 cassé..."
            rows={3}
            className="w-full bg-gray-800 border border-gray-700 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-orange-400 resize-none" />
        </div>
      )}

      {/* ── MODE PHOTO ── */}
      {videoMode === 'photo' && (
        <UploadZone label="Image de référence" value={refImage} onChange={setRefImage} id="vid-ref" />
      )}
      {videoMode === 'photo' && (
        <div>
          <label className="text-gray-400 text-xs block mb-1.5">Description (optionnelle)</label>
          <input type="text" value={prompt} onChange={e => setPrompt(e.target.value)}
            placeholder="ex: téléphone réparé, animation dynamique..."
            className="w-full bg-gray-800 border border-gray-700 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-orange-400" />
        </div>
      )}

      {/* Duration */}
      <div>
        <label className="text-gray-400 text-xs block mb-2">Durée</label>
        <div className="flex gap-2">
          {VIDEO_DURATIONS.map(d => (
            <button key={d.id} onClick={() => setDuration(d.id)}
              className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all text-center ${duration === d.id ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'}`}>
              <div className="font-bold">{d.label}</div>
              <div className="text-xs opacity-60">{d.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Format */}
      <div>
        <label className="text-gray-400 text-xs block mb-2">Format</label>
        <div className="flex gap-2">
          {VIDEO_FORMATS.map(f => (
            <button key={f.id} onClick={() => setFormat(f.id)}
              className={`flex-1 py-2 rounded-xl text-xs font-medium transition-all text-center ${format === f.id ? 'bg-green-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'}`}>
              <div>{f.label}</div><div className="opacity-60">{f.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Model */}
      <div>
        <label className="text-gray-400 text-xs block mb-2">Modèle IA</label>
        <div className="flex gap-2">
          {VIDEO_MODELS.map(m => (
            <button key={m.id} onClick={() => setVideoModel(m.id)}
              className={`flex-1 py-2.5 rounded-xl text-xs font-medium transition-all text-center ${videoModel === m.id ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'}`}>
              <div className="font-semibold">{m.label}</div>
              <div className="opacity-60">{m.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Generate button */}
      <button onClick={startVideoJob} disabled={generating} data-testid="generate-video-btn"
        className={`w-full font-bold py-4 rounded-2xl flex items-center justify-center gap-2 text-base transition-all active:scale-[0.98] disabled:opacity-50
          ${videoMode === 'capcut'
            ? 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white'
            : 'bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white'}`}>
        {generating ? (
          <><div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
            <span>{videoMode === 'capcut' ? `CapCut en cours... ${fmtTime(elapsed)}` : `Génération... ${fmtTime(elapsed)}`}</span></>
        ) : (
          <><Video size={18} />
            <span>{videoMode === 'capcut' ? '🎬 Générer le montage CapCut' : 'Générer la vidéo'}</span></>
        )}
      </button>

      {/* Progress */}
      {generating && (
        <div className="bg-orange-900/20 border border-orange-500/30 rounded-2xl p-4 text-center">
          <div className="flex items-center justify-center gap-2 text-orange-300 mb-2">
            <Clock size={16} />
            <span className="text-sm font-medium">Sora 2 en cours de génération</span>
          </div>
          <p className="text-gray-400 text-xs">La génération vidéo prend 2 à 5 minutes selon la durée et le modèle choisi.</p>
          <div className="mt-3 w-full bg-gray-800 rounded-full h-1.5">
            <div className="bg-gradient-to-r from-red-500 to-orange-400 h-1.5 rounded-full animate-pulse" style={{ width: `${Math.min(elapsed / 300 * 100, 90)}%` }} />
          </div>
        </div>
      )}

      {/* Result */}
      {jobStatus === 'completed' && videoB64 && (
        <div className="space-y-3">
          <div className="rounded-2xl overflow-hidden border border-red-500/30 bg-gray-900">
            <video controls className="w-full" src={`data:video/mp4;base64,${videoB64}`} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={downloadVideo}
              className="flex items-center justify-center gap-2 bg-green-700 hover:bg-green-800 text-white rounded-xl py-3 text-sm font-medium transition-all active:scale-95">
              <Download size={16} /> Télécharger
            </button>
            <button onClick={saveVideo}
              className="flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl py-3 text-sm font-medium transition-all active:scale-95">
              <FolderOpen size={16} /> Sauvegarder
            </button>
          </div>
        </div>
      )}
      {jobStatus === 'failed' && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-2xl p-4 text-center text-red-300 text-sm">
          Génération échouée. Réessayez avec un prompt plus simple.
        </div>
      )}
    </div>
  );
};

// ── PROJECTS TAB ───────────────────────────────────────────────────────────
const ProjectsTab = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchProjects = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/studio/projects`, { headers: authHeaders() });
      if (resp.ok) setProjects(await resp.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const deleteProject = async (id) => {
    if (!window.confirm('Supprimer ce projet ?')) return;
    await fetch(`${API_URL}/api/studio/projects/${id}`, { method: 'DELETE', headers: authHeaders() });
    setProjects(prev => prev.filter(p => p.id !== id));
    if (selected?.id === id) setSelected(null);
    toast.success('Projet supprimé');
  };

  const downloadProject = (p) => {
    const a = document.createElement('a');
    a.href = p.full_base64 || p.thumbnail_base64;
    a.download = `${p.label}.${p.type === 'video' ? 'mp4' : 'png'}`.replace(/\s+/g, '-');
    a.click();
  };

  if (loading) return <div className="flex items-center justify-center h-40 text-gray-400 text-sm">Chargement...</div>;

  if (projects.length === 0) return (
    <div className="flex flex-col items-center justify-center h-52 text-center">
      <FolderOpen size={48} className="text-gray-700 mb-3" />
      <p className="text-gray-500 text-sm">Aucun projet sauvegardé</p>
      <p className="text-gray-600 text-xs mt-1">Générez du contenu et cliquez "Sauvegarder"</p>
    </div>
  );

  return (
    <div className="space-y-4">
      {selected && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-semibold text-sm">{selected.label}</h3>
            <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-white text-xs">← Retour</button>
          </div>
          {selected.type === 'video' ? (
            <video controls className="w-full rounded-2xl border border-gray-700" src={selected.full_base64} />
          ) : (
            <img src={selected.full_base64 || selected.thumbnail_base64} alt={selected.label} className="w-full rounded-2xl border border-gray-700 max-h-96 object-contain bg-gray-900" />
          )}
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => downloadProject(selected)}
              className="flex items-center justify-center gap-2 bg-green-700 hover:bg-green-800 text-white rounded-xl py-3 text-sm font-medium">
              <Download size={15} /> Télécharger
            </button>
            <button onClick={() => deleteProject(selected.id)}
              className="flex items-center justify-center gap-2 bg-red-700 hover:bg-red-800 text-white rounded-xl py-3 text-sm font-medium">
              <Trash2 size={15} /> Supprimer
            </button>
          </div>
        </div>
      )}

      {!selected && (
        <>
          <div className="flex items-center justify-between">
            <p className="text-gray-400 text-xs">{projects.length} projet{projects.length > 1 ? 's' : ''} sauvegardé{projects.length > 1 ? 's' : ''}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {projects.map(p => (
              <div key={p.id} className="group relative rounded-2xl overflow-hidden border border-gray-700 bg-gray-900 cursor-pointer hover:border-purple-500 transition-all"
                onClick={() => setSelected(p)}>
                {p.type === 'video' ? (
                  <div className="h-28 flex items-center justify-center bg-gray-800">
                    <Play size={32} className="text-red-400" />
                  </div>
                ) : (
                  <img src={p.thumbnail_base64} alt={p.label} className="w-full h-28 object-cover" />
                )}
                <div className="p-2">
                  <p className="text-white text-xs font-medium truncate">{p.label}</p>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-gray-500 text-xs">{new Date(p.created_at).toLocaleDateString('fr-FR')}</p>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${p.type === 'video' ? 'bg-red-900/50 text-red-300' : 'bg-purple-900/50 text-purple-300'}`}>
                      {p.type === 'video' ? '🎥' : '📸'}
                    </span>
                  </div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); deleteProject(p.id); }}
                  className="absolute top-2 right-2 bg-red-600/80 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ── MAIN COMPONENT ─────────────────────────────────────────────────────────
export const StudioPhotos = () => {
  const [tab, setTab] = useState('canvas');

  const TABS = [
    { id: 'canvas', label: 'Éditeur', icon: Palette, color: 'yellow' },
    { id: 'photo', label: 'Photo IA', icon: ImageIcon, color: 'purple' },
    { id: 'video', label: 'Vidéo', icon: Video, color: 'red' },
    { id: 'projects', label: 'Projets', icon: FolderOpen, color: 'blue' },
  ];

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-xl bg-purple-600/20 flex items-center justify-center">
            <Camera size={18} className="text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Studio Photos & Vidéo IA</h2>
            <p className="text-gray-500 text-xs">Contenus viraux pour Instagram, Facebook & Reels</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-800/60 p-1 rounded-2xl mb-5">
        {TABS.map(t => {
          const Icon = t.icon;
          const isActive = tab === t.id;
          const activeColors = { yellow: 'bg-yellow-500', purple: 'bg-purple-600', red: 'bg-red-600', blue: 'bg-blue-600' };
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-[0.97]
                ${isActive ? `${activeColors[t.color]} text-white shadow-lg` : 'text-gray-400 hover:text-gray-200'}`}
            >
              <Icon size={15} />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="pb-8">
        {tab === 'canvas' && <StudioCanvas />}
        {tab === 'photo' && <PhotoTab />}
        {tab === 'video' && <VideoTab />}
        {tab === 'projects' && <ProjectsTab />}
      </div>
    </div>
  );
};
